import {
  faCheck, faFaceGrinWide, faFaceSunglasses,
} from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert/alert.component';
import { ButtonComponent } from '~/_shared/baseComponents/buttons';
import { FormTextInputComponent } from '~/_shared/baseComponents/inputs';
import {
  accountSettingsModalContentStyle, footerButtonStyle, formStyle, hiddenStyle,
} from '~/_shared/components/accountSettings/modalDialogs/shared.styles';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { autoFocus } from '~/_shared/utils/form/form.helpers';
import { FormFieldType } from '~/_shared/utils/form/formValidation.helpers';
import { useFormFieldValidation } from '~/_shared/utils/form/useFormFieldValidation';
import { useTranslation } from '~/_shared/utils/hooks';
import { normalizeWhitespace } from '~/_shared/utils/text/text.helpers';
import type { ModalProps } from '~/modal/modalType.enum';

export type MissingUserInfoModalProps = ModalProps & {
  firstName?: string;
  lastName?: string;
  onSubmit: (firstName: string, lastName: string) => Promise<void>;
};

export const MissingUserInfoModalComponent = (props: MissingUserInfoModalProps) => {
  const [t] = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { value: firstName, onChange: onFirstNameChange, onBlur: onFirstNameBlur, errors: firstNameErrors, validateValue: validateFirstName } = useFormFieldValidation({
    type: FormFieldType.Required,
    initialValue: props.firstName,
    requiredFieldErrorMessage: t('First name must not be empty'),
  });
  const { value: lastName, onChange: onLastNameChange, onBlur: onLastNameBlur, errors: lastNameErrors, validateValue: validateLastName } = useFormFieldValidation({
    type: FormFieldType.Required,
    initialValue: props.lastName,
    requiredFieldErrorMessage: t('Last name must not be empty'),
  });

  const handleConfirm = async () => {
    const firstErrors = validateFirstName(firstName);
    const lastErrors = validateLastName(lastName);

    if (firstErrors.length === 0 && lastErrors.length === 0) {
      setIsLoading(true);
      setError(null);
      try {
        await props.onSubmit(normalizeWhitespace(firstName), normalizeWhitespace(lastName));
        props.onClose();
      }
      catch {
        setError(t('Something went wrong, please try again.'));
      }
      finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <ModalComponent
      caption={t('missingUserInfoModal.caption')}
      contentStyle={accountSettingsModalContentStyle}
      footer={(
        <ButtonComponent
          css={footerButtonStyle}
          isDisabled={isLoading}
          onClick={handleConfirm}
          prefixIcon={faCheck}
          text={t('Confirm')}
        />
      )}
      additionalContent={isLoading ? <OverlayLoaderComponent /> : null}
      key="success"
      isCloseDisabled
      isOpen={props.isOpen}
      onClose={props.onClose}
      preventCloseOnOverlayClick
    >
      <form
        css={formStyle}
        onSubmit={isLoading ? undefined : (e) => {
          e.preventDefault();
          handleConfirm();
        }}
      >
        {t('missingUserInfoModal.explanation')}
        {error && (
          <AlertComponent type="danger">
            {error}
          </AlertComponent>
        )}
        <FormTextInputComponent
          setInputRef={autoFocus}
          errorMessages={firstNameErrors}
          icon={faFaceGrinWide}
          label={t('First Name')}
          onBlur={onFirstNameBlur}
          onChange={onFirstNameChange}
          placeholder={t('First Name')}
          value={firstName}
        />
        <FormTextInputComponent
          errorMessages={lastNameErrors}
          icon={faFaceSunglasses}
          label={t('Last Name')}
          onBlur={onLastNameBlur}
          onChange={onLastNameChange}
          placeholder={t('Last Name')}
          value={lastName}
        />
        {!isLoading && (
          // This makes the form submit on Enter
          <input type="submit" css={hiddenStyle} />
        )}
      </form>
    </ModalComponent>
  );
};
